import { H4 } from '@/components/Core/Heading';
import type { IImage } from '@/interfaces';
import classNames from '@/utils/classNames';
import { adjustImageAsset } from '@/utils/imageUtils';
import { ensureArray } from '@/utils/siteUtils';
import type { AnchorHTMLAttributes, CSSProperties, ElementType, FC, HTMLAttributes, ReactNode } from 'react';
import { Children, Fragment, useId } from 'react';
import type { FCC, FCl } from 'react-fc';
import styles from './Card.module.css';

import animationStyles from '@/components/Atoms/Animation/Animation.module.css';

type AnchorProps = AnchorHTMLAttributes<HTMLAnchorElement> & Record<`data-${string}`, string>;

interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title' | 'prefix'> {
    title: ReactNode;
    headerImage?: string | [string, string];
    withImage?: boolean | 'flip';
    /* Whether the card should become horizontal when there is enough space */
    responsive?: boolean;
    /* Use dynamic secondary background color to make the card look boxed */
    boxed?: boolean | 'current';
    headingLevel?: ElementType;
    href?: string | ((props: AnchorProps) => ReactNode);
    prefix?: ReactNode;
    children?: ReactNode;
    linkProps?: AnchorProps;
}

/**
 * A content box used to display information about a single item.
 */
export const Card: FCC<CardProps> = ({
    className,
    headingLevel = 'h3',
    headerImage,
    children,
    title,
    id,
    href,
    prefix,
    boxed,
    responsive = true,
    withImage = true,
    linkProps = {},
    ...rest
}) => {
    const defaultId = useId();
    id = id || defaultId;
    if (typeof href === 'string') {
        title = (
            <a className="bbui-card-primary-action" href={href} {...linkProps}>
                {title}
            </a>
        );
    } else if (typeof href === 'function') {
        title = href({ ...linkProps, className: 'bbui-card-primary-action', children: title });
    }

    let headerImageSet = undefined;
    if (headerImage) {
        headerImageSet = `image-set(${ensureArray(headerImage)
            .map((url, i) => `url('${url}') ${i + 1}x`)
            .join(', ')})`;
    }

    return (
        <section
            aria-labelledby={id}
            className={classNames(
                'bbui-card',
                styles.card,
                {
                    [styles.withImage]: withImage === true,
                    [styles.imageFlip]: withImage === 'flip',
                    [styles.boxed]: boxed,
                    [styles.responsive]: responsive,
                    // if boxed is 'current', we want to keep the original background
                    'cds-background-flip': boxed === true,
                },
                className
            )}
            {...rest}
        >
            <div className={classNames('bbui-card-contents', styles.contentsBox, styles.contents)}>
                <div
                    className={classNames(styles.header, { [styles.headerImage]: headerImageSet })}
                    data-cds-theme={headerImageSet ? 'dark' : undefined}
                    style={
                        {
                            '--_header-image-set': headerImageSet,
                        } as CSSProperties
                    }
                >
                    {prefix}
                    <H4 id={id} as={headingLevel} className={styles.heading}>
                        {title}
                    </H4>
                </div>
                {children}
            </div>
        </section>
    );
};

export const ImageCard: FC<{ image: IImage } & CardProps> = ({ image, className, style = {}, ...rest }) => (
    <Card
        data-cds-theme="dark"
        {...rest}
        className={classNames(styles.imageCard, className)}
        style={{
            ...style,
            backgroundImage: `image-set(
                url('${adjustImageAsset(image.baseUrl, 200)}') 1x,
                url('${adjustImageAsset(image.baseUrl, 400)}') 2x
            )`,
        }}
    />
);

export const CardCategory: FCC<HTMLAttributes<HTMLDivElement>> = ({ className, children, ...rest }) => (
    <div className={classNames('bbui-card-category', 'cds-t-group-heading2', className)} {...rest}>
        {children}
    </div>
);

export const CardBody: FCC<HTMLAttributes<HTMLDivElement>> = ({ className, children, ...rest }) => (
    <div className={classNames('bbui-card-body', styles.body, className)} {...rest}>
        {children}
    </div>
);

export const CardImage: FCC<HTMLAttributes<HTMLDivElement> & { border?: boolean }> = ({
    className,
    border,
    ...rest
}) => (
    <div
        className={classNames('bbui-card-image', styles.image, { [styles.imageBorder]: border }, className)}
        {...rest}
    />
);

export const CardTags: FCC<HTMLAttributes<HTMLUListElement>> = ({ className, children, ...rest }) => (
    <ul className={classNames('bbui-card-tags', styles.tags, className)} {...rest}>
        {Children.map(children, (child: ReactNode) => child && <li>{child}</li>)}
    </ul>
);

export const CardSubTitle: FCC<HTMLAttributes<HTMLParagraphElement>> = ({ className, children, ...rest }) => (
    <p className={classNames('bbui-card-subtitle', className)} {...rest}>
        {children}
    </p>
);

export type CardMetadataItem = { icon?: ReactNode; label: ReactNode; value: ReactNode };

export const CardMetadata: FCl<
    { animate?: boolean; items: (CardMetadataItem | undefined)[] } & HTMLAttributes<HTMLDListElement>
> = ({ className, animate, items, ...rest }) =>
    items.filter(Boolean).length > 0 ? (
        <dl
            className={classNames(
                'cds-t-body-small',
                'bbui-card-metadata',
                styles.metadata,
                { [animationStyles.fadeChildren]: animate },
                className
            )}
            {...rest}
        >
            {items.filter(Boolean).map(
                (item, i) =>
                    item && (
                        <Fragment key={i}>
                            <dt>
                                {item.icon} {item.label}
                            </dt>
                            <dd>{item.value}</dd>
                        </Fragment>
                    )
            )}
        </dl>
    ) : null;

export const CardDescription: FCC<
    {
        lines?: false | number;
    } & HTMLAttributes<HTMLParagraphElement>
> = ({ className, lines = 4, children, style, ...rest }) => (
    <p
        className={classNames(
            'bbui-card-description',
            'cds-t-body',
            styles.description,
            lines && styles.descriptionClamped,
            className
        )}
        style={{
            ...style,
            ...(lines && { WebkitLineClamp: lines }),
        }}
        {...rest}
    >
        {children}
    </p>
);

export const CardActions: FCC<HTMLAttributes<HTMLUListElement>> = ({ className, children, ...rest }) => (
    <ul className={classNames('bbui-card-actions', styles.actions, className)} {...rest}>
        {Children.map(children, (child: ReactNode) => child && <li>{child}</li>)}
    </ul>
);
